import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Coins, Send, UserCheck, Building2, Briefcase, Users, Clock, CreditCard,
  RefreshCw, TrendingUp, Percent, Layers, Award, ShieldCheck, Wallet,
  Mail, BarChart3, Sparkles, IndianRupee, Search, Calendar, CalendarPlus,
  BadgeCheck, ChevronRight, Activity, MapPin, Plus, Zap
} from 'lucide-react';
import {
  RecruiterDashboardResponse,
  TeamMemberPerformance,
  CountItem,
} from '../Types/RecruiterDashboardType';

interface RecruiterDashboardComponentProps {
  data: RecruiterDashboardResponse | null;
  loading: boolean;
  onRefresh: () => void;
}

/* ─── Reusable building blocks ─────────────────────────────────────── */

const StatCard = ({
  label, value, icon, gradient, sub,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  sub?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${gradient}`}
  >
    <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
    <div className="relative z-10 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="rounded-lg bg-white/20 p-2 backdrop-blur-md">{icon}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/80">Live</span>
      </div>
      <div>
        <h4 className="text-3xl font-black">{value}</h4>
        <p className="text-sm font-bold opacity-90">{label}</p>
      </div>
      {sub && <p className="text-[10px] font-medium text-white/70 italic">{sub}</p>}
    </div>
  </motion.div>
);

const SectionCard = ({
  title, icon, children, right,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
}) => (
  <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          {icon}
        </span>
        <h3 className="text-base font-bold text-slate-800">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const MiniStat = ({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) => (
  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50">
    <span className="flex items-center gap-2 text-xs font-semibold text-slate-500">
      {icon}
      {label}
    </span>
    <span className="text-sm font-black text-slate-800">{value}</span>
  </div>
);

const BarList = ({ items, color = 'bg-indigo-500' }: { items: CountItem[]; color?: string }) => {
  const max = useMemo(() => Math.max(1, ...(items || []).map((i) => i.value)), [items]);
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-400">No data yet.</p>;
  }
  return (
    <div className="flex flex-col gap-2.5">
      {items.map((item, idx) => (
        <div key={`${item.label}-${idx}`} className="flex items-center gap-3">
          <span className="w-32 truncate text-xs font-semibold text-slate-600 text-right">{item.label}</span>
          <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${color} rounded-full transition-all duration-700`}
              style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }}
            />
          </div>
          <span className="w-10 text-xs font-bold text-slate-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

const FunnelRow = ({
  label, value, percentage, color = 'bg-indigo-500',
}: {
  label: string;
  value: number;
  percentage: number;
  color?: string;
}) => (
  <div className="text-xs">
    <div className="flex justify-between text-slate-600 font-medium mb-1">
      <span className="truncate">{label}</span>
      <span>{value} <span className="text-slate-400 font-normal">({percentage}%)</span></span>
    </div>
    <div className="w-full bg-slate-100 rounded-full h-1.5">
      <div className={`${color} h-1.5 rounded-full`} style={{ width: `${percentage}%` }} />
    </div>
  </div>
);

const CompactStatCard = ({
  icon: Icon, title, value, change, iconBg, iconColor,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string;
  change: string;
  iconBg: string;
  iconColor: string;
}) => (
  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between hover:shadow-md transition">
    <div className="flex items-center justify-between mb-2">
      <div className={`p-2 rounded-lg ${iconBg} ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div>
      <p className="text-xs font-medium text-slate-400 uppercase tracking-tight">{title}</p>
      <h3 className="text-xl font-black text-slate-900 my-0.5">{value}</h3>
      <p className="text-[10px] text-slate-500 font-medium">{change}</p>
    </div>
  </div>
);

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

const formatCurrency = (value: number): string =>
  `₹${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const navigateTo = (path: string) => {
  window.location.href = path;
};

const pctOf = (value: number, total: number): number => (total > 0 ? Math.round((value / total) * 100) : 0);

const sumOf = (items: { value: number }[] | undefined): number =>
  (items || []).reduce((s, x) => s + (Number(x?.value) || 0), 0);

const TeamRow = ({ member }: { member: TeamMemberPerformance }) => {
  const total = Math.max(member.sent, 1);
  const pct = (v: number) => Math.round((v / total) * 100);

  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
      <td className="py-3 pl-2">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center text-xs font-black">
            {member.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?'}
          </span>
          <div>
            <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
              {member.name}
              {member.admin && <BadgeCheck size={14} className="text-indigo-500" />}
            </p>
            <p className="text-xs text-slate-400">{member.email}</p>
          </div>
        </div>
      </td>
      <td className="py-3">
        <p className="text-xs font-semibold text-slate-600">{member.designation || 'Recruiter'}</p>
        <span
          className={`mt-1 inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${
            member.status ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {member.status ? 'ACTIVE' : 'INACTIVE'}
        </span>
      </td>
      <td className="py-3 text-center">
        <p className="text-sm font-black text-slate-800">{member.sent}</p>
        <p className="text-[10px] text-slate-400">sent</p>
      </td>
      <td className="py-3 text-center">
        <p className="text-sm font-black text-amber-600">{member.pending}</p>
        <div className="w-14 mx-auto h-1 rounded-full bg-slate-100">
          <div className="h-1 rounded-full bg-amber-400" style={{ width: `${pct(member.pending)}%` }} />
        </div>
      </td>
      <td className="py-3 text-center">
        <p className="text-sm font-black text-emerald-600">{member.accepted}</p>
        <div className="w-14 mx-auto h-1 rounded-full bg-slate-100">
          <div className="h-1 rounded-full bg-emerald-400" style={{ width: `${pct(member.accepted)}%` }} />
        </div>
      </td>
      <td className="py-3 text-center">
        <p className="text-sm font-black text-rose-500">{member.rejected}</p>
        <div className="w-14 mx-auto h-1 rounded-full bg-slate-100">
          <div className="h-1 rounded-full bg-rose-400" style={{ width: `${pct(member.rejected)}%` }} />
        </div>
      </td>
    </tr>
  );
};

/* ─── Main Dashboard ────────────────────────────────────────────────── */

function RecruiterDashboardComponent({ data, loading, onRefresh }: RecruiterDashboardComponentProps) {
  const [lastUpdated] = React.useState<string>(new Date().toLocaleString());

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#F8FAFC] p-6 md:p-8 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-slate-400">
          <RefreshCw className="w-10 h-10 animate-spin text-indigo-500" />
          <p className="text-sm font-semibold">Loading your recruitment dashboard…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[60vh] bg-[#F8FAFC] p-6 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-bold text-slate-600 mb-2">Unable to load dashboard</p>
          <button
            onClick={onRefresh}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const p = data.profile;
  const co = data.creditOverview;
  const ra = data.recruitmentActivity;
  const cr = data.companyRecruitment;
  const jt = data.jobTemplates;
  const isAdmin = p.companyAdmin;

  const myTotal = Math.max(ra.totalSent, 1);
  const funnel = (v: number) => Math.round((v / myTotal) * 100);
  const cpi = data.candidatePoolInsights;
  const trend = cpi?.inviteAcceptanceTrend || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center">
                  <BarChart3 size={24} />
                </span>
                Recruiter Dashboard
              </h1>
              <p className="text-slate-500 mt-2 font-medium">
                Hi <span className="font-bold text-slate-700">{p.firstName || 'there'}</span>
                {isAdmin && <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-indigo-50 text-indigo-600">
                  <ShieldCheck size={12} /> RECRUITER ADMIN
                </span>}
                {' — '}{p.companyName || 'Your company'} • {p.designation || 'Recruiter'}
              </p>
            </div>
            <button
              onClick={onRefresh}
              className="px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors flex items-center gap-2 self-start md:self-center shadow-sm"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400 font-medium">Last updated: {lastUpdated}</p>
        </header>

        {/* ── KPI Row ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <StatCard
            label="Credits Left"
            value={co.creditsRemaining.toLocaleString()}
            icon={<Coins className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-amber-500 to-orange-600"
            sub={`${co.usagePercent}% used of ${co.creditsPurchased.toLocaleString()} purchased`}
          />
          <StatCard
            label="Invitations (You)"
            value={ra.totalSent.toLocaleString()}
            icon={<Send className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-fuchsia-500 to-purple-700"
            sub={`${ra.pending} pending • ${ra.accepted} accepted`}
          />
          <StatCard
            label="Hired / Accepted"
            value={ra.hiredCandidates.toLocaleString()}
            icon={<UserCheck className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
            sub={`${ra.acceptanceRate}% acceptance rate`}
          />
          <StatCard
            label="Company Invitations"
            value={cr.totalSent.toLocaleString()}
            icon={<Building2 className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-blue-600 to-indigo-700"
            sub={isAdmin
              ? `${cr.activeRecruiters} recruiters in your team`
              : `${cr.invitationsThisMonth.toLocaleString()} sent this month`}
          />
        </div>

        {/* ── Credit & Subscription / My Activity ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SectionCard
            title="Credits & Subscription"
            icon={<Wallet size={18} />}
            right={<span className="text-xs font-bold text-slate-400">Company</span>}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Credits Purchased" value={co.creditsPurchased.toLocaleString()} icon={<CreditCard size={14} />} />
              <MiniStat label="Credits Used" value={co.creditsUsed.toLocaleString()} icon={<Activity size={14} />} />
              <MiniStat label="Credits Remaining" value={co.creditsRemaining.toLocaleString()} icon={<Coins size={14} />} />
              <MiniStat label="Total Spent" value={formatCurrency(co.totalAmountPaid)} icon={<IndianRupee size={14} />} />
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-600">{co.subscriptionPlanName || 'No active plan'}</span>
                <span className="text-xs font-black text-indigo-600">{co.usagePercent}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, Math.max(0, co.usagePercent))}%` }}
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock size={12} /> Expires {formatDate(co.subscriptionPlanExpireDate)}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full font-bold ${
                    (co.subscriptionPlanStatus || '').toUpperCase() === 'ACTIVE'
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {co.subscriptionPlanStatus || 'N/A'}
                </span>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="My Recruitment Activity"
            icon={<Send size={18} />}
            right={<span className="text-xs font-bold text-slate-400">Your pipeline</span>}
          >
            <div className="flex flex-col gap-3 mb-4">
              <FunnelRow label="Pending" value={ra.pending} percentage={funnel(ra.pending)} color="bg-amber-400" />
              <FunnelRow label="Accepted" value={ra.accepted} percentage={funnel(ra.accepted)} color="bg-emerald-400" />
              <FunnelRow label="Rejected" value={ra.rejected} percentage={funnel(ra.rejected)} color="bg-rose-400" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Sent Today" value={ra.invitationsToday} icon={<Clock size={14} />} />
              <MiniStat label="Sent This Week" value={ra.invitationsThisWeek} icon={<Clock size={14} />} />
              <MiniStat label="Sent This Month" value={ra.invitationsThisMonth} icon={<CalendarPlus size={14} />} />
              <MiniStat label="Avg Response Time" value={formatDuration(ra.avgResponseTimeSeconds)} icon={<TrendingUp size={14} />} />
            </div>
          </SectionCard>
        </div>

        {/* ── Company Overview / Job Templates ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SectionCard
            title="Company Overview"
            icon={<Building2 size={18} />}
            right={<span className="text-xs font-bold text-slate-400">All recruiters</span>}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Total Invitations" value={cr.totalSent.toLocaleString()} icon={<Send size={14} />} />
              <MiniStat label="Accepted / Hired" value={cr.hiredCandidates.toLocaleString()} icon={<UserCheck size={14} />} />
              <MiniStat label="Active Recruiters" value={cr.activeRecruiters.toLocaleString()} icon={<Users size={14} />} />
              <MiniStat label="Acceptance Rate" value={`${cr.acceptanceRate}%`} icon={<Percent size={14} />} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Sent Today" value={cr.invitationsToday} icon={<Clock size={14} />} />
              <MiniStat label="Sent This Week" value={cr.invitationsThisWeek} icon={<Clock size={14} />} />
              <MiniStat label="Sent This Month" value={cr.invitationsThisMonth} icon={<CalendarPlus size={14} />} />
              <MiniStat label="Avg Response Time" value={formatDuration(cr.avgResponseTimeSeconds)} icon={<TrendingUp size={14} />} />
            </div>
          </SectionCard>

          <SectionCard
            title="Job Templates"
            icon={<Briefcase size={18} />}
            right={<span className="text-xs font-bold text-slate-400">Company</span>}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Total Jobs" value={jt.total.toLocaleString()} icon={<Layers size={14} />} />
              <MiniStat label="Active Jobs" value={jt.active.toLocaleString()} icon={<Briefcase size={14} />} />
              <MiniStat label="Created This Week" value={jt.createdThisWeek.toLocaleString()} icon={<Sparkles size={14} />} />
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs font-bold text-slate-500 mb-3">Openings by Role Level</p>
              <BarList items={jt.byRoleLevel} color="bg-blue-500" />
            </div>
          </SectionCard>
        </div>

        {/* ── Team Performance (admin only) ── */}
        {isAdmin && data.teamPerformance.length > 0 && (
          <div className="mb-8">
            <SectionCard
              title="Team Performance"
              icon={<Users size={18} />}
              right={<span className="text-xs font-bold text-slate-400">{data.teamPerformance.length} members</span>}
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      <th className="py-2 pl-2 font-bold">Recruiter</th>
                      <th className="py-2 font-bold">Role</th>
                      <th className="py-2 text-center font-bold">Sent</th>
                      <th className="py-2 text-center font-bold">Pending</th>
                      <th className="py-2 text-center font-bold">Accepted</th>
                      <th className="py-2 text-center font-bold">Rejected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.teamPerformance.map((member) => (
                      <TeamRow key={member.recruiterId} member={member} />
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )}

        {/* ── Quick Actions ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Search Candidates', path: '/recruiter/candidates/listing', icon: <Search className="w-4 h-4 text-blue-500" /> },
            { label: 'Job Management', path: '/recruiter/job-management', icon: <Briefcase className="w-4 h-4 text-emerald-500" /> },
            // Purchasing is a company decision, so the plan is offered to the
            // company admin alone.
            ...(isAdmin
              ? [{ label: 'Subscription Plans', path: '/recruiter/subscription-plans', icon: <CreditCard className="w-4 h-4 text-amber-500" /> }]
              : []),
            isAdmin
              ? { label: 'Manage Recruiters', path: '/recruiter/manage-recruiter', icon: <Users className="w-4 h-4 text-violet-500" /> }
              : { label: 'My Invitations', path: '/recruiter/invitations', icon: <Mail className="w-4 h-4 text-fuchsia-500" /> },
          ].map((action) => (
            <button
              key={action.label}
              onClick={() => navigateTo(action.path)}
              className="group flex items-center justify-between px-4 py-3.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all text-sm font-bold text-slate-700"
            >
              <span className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                  {action.icon}
                </span>
                {action.label}
              </span>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
            </button>
          ))}
        </div>

        {/* ── Candidate Pool Insights (mock preview from /test component) ── */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                Candidate Pool Insights
                <span className="text-xs font-normal text-slate-400 border border-slate-200 rounded-full px-1.5">i</span>
              </h2>
              <p className="text-sm text-slate-500">Live analytics of the candidate talent pool</p>
            </div>
          </div>

          {/* Pool Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            <CompactStatCard icon={Users} title="Total Candidates" value={(cpi?.overview.totalCandidates ?? 0).toLocaleString()} change={`${cpi?.activityAnalytics.newRegistrations30Days ?? 0} new in 30 days`} iconBg="bg-violet-100" iconColor="text-violet-600" />
            <CompactStatCard icon={Award} title="10+ Years Experience" value={(cpi?.overview.seniorCandidates ?? 0).toLocaleString()} change={cpi?.overview.totalCandidates ? `${Math.round((cpi.overview.seniorCandidates / cpi.overview.totalCandidates) * 100)}% of pool` : '—'} iconBg="bg-emerald-100" iconColor="text-emerald-600" />
            <CompactStatCard icon={Calendar} title="Active This Month" value={(cpi?.overview.activeThisMonth ?? 0).toLocaleString()} change={`${cpi?.activityAnalytics.profileUpdates30Days ?? 0} updated recently`} iconBg="bg-amber-100" iconColor="text-amber-600" />
            <CompactStatCard icon={Zap} title="Available Immediately" value={(cpi?.overview.availableImmediately ?? 0).toLocaleString()} change="Explorers ready now" iconBg="bg-blue-100" iconColor="text-blue-600" />
            <CompactStatCard icon={Mail} title="Invite Acceptance Rate" value={`${cpi?.overview.inviteAcceptanceRate ?? 0}%`} change={`${cpi?.activityAnalytics.inviteAcceptance30Days ?? 0}% in last 30 days`} iconBg="bg-teal-100" iconColor="text-teal-600" />
            <CompactStatCard icon={Clock} title="Average Response Time" value={formatDuration(cpi?.overview.avgResponseTimeSeconds ?? null)} change="Global average" iconBg="bg-rose-100" iconColor="text-rose-600" />
          </div>

          {/* Talent Insights */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Experience Distribution</h3>
              <div className="space-y-3">
                {(cpi?.experienceDistribution || []).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, cpi?.overview.totalCandidates ?? 0)} color={['bg-blue-500', 'bg-cyan-500', 'bg-amber-500', 'bg-indigo-600'][i % 4]} />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View details →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Skills Distribution (Top 6)</h3>
              <div className="space-y-3">
                {(cpi?.skillsDistribution || []).slice(0, 6).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.skillsDistribution))} color="bg-blue-600" />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View all candidates →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Location Distribution</h3>
              <div className="space-y-3">
                {(cpi?.locationDistribution || []).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.locationDistribution))} color="bg-blue-600" />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View locations →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Availability (Notice Period)</h3>
              <div className="space-y-3">
                {(cpi?.availability || []).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.availability))} color={['bg-emerald-500', 'bg-blue-400', 'bg-amber-400', 'bg-slate-400', 'bg-indigo-600'][i % 5]} />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View availability →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Current Roles (Top 6)</h3>
              <div className="space-y-3">
                {(cpi?.currentRoles || []).slice(0, 6).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.currentRoles))} color="bg-blue-600" />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View all roles →</button>
            </div>
          </div>

          {/* Deeper Analytics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Activity Analytics</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-1 border-b border-slate-100">
                  <span className="text-slate-500">Active Last 7 Days</span>
                  <span className="font-semibold text-slate-800">{(cpi?.activityAnalytics.activeLast7Days ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-100">
                  <span className="text-slate-500">Active Last 30 Days</span>
                  <span className="font-semibold text-slate-800">{(cpi?.activityAnalytics.activeLast30Days ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-100">
                  <span className="text-slate-500">Profile Updates (30 Days)</span>
                  <span className="font-semibold text-slate-800">{(cpi?.activityAnalytics.profileUpdates30Days ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-100">
                  <span className="text-slate-500">New Registrations (30 Days)</span>
                  <span className="font-semibold text-slate-800">{(cpi?.activityAnalytics.newRegistrations30Days ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-slate-500">Invite Acceptance (30 Days)</span>
                  <span className="font-semibold text-slate-800">{cpi?.activityAnalytics.inviteAcceptance30Days ?? 0}%</span>
                </div>
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View active candidates →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-slate-800">Invite Acceptance Trends</h3>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{trend.length > 0 ? `${trend[trend.length - 1].value}%` : '—'}</span>
              </div>
              <div className="h-24 w-full flex items-end gap-1 px-2 pt-4">
                {trend.length > 0 ? (
                  trend.map((point, i) => (
                    <div
                      key={i}
                      className="bg-blue-100 hover:bg-blue-500 transition-all rounded-t"
                      style={{ height: `${Math.max(4, Math.min(100, point.value))}%`, width: '100%' }}
                      title={`${point.label}: ${point.value}%`}
                    ></div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic">No acceptance data yet.</p>
                )}
              </div>
              <div className="flex justify-between text-[10px] text-slate-400 mt-2">
                {trend.map((point, i) => (
                  <span key={i}>{point.label}</span>
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View detailed report →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Profile Completeness</h3>
              <div className="space-y-3">
                {(cpi?.profileCompleteness || []).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.profileCompleteness))} color={['bg-teal-500', 'bg-blue-400', 'bg-amber-400', 'bg-rose-400'][i % 4]} />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View incomplete profiles →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Experience Level (10+ Years)</h3>
              <div className="space-y-3">
                {(cpi?.experienceLevel || []).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.experienceLevel))} color="bg-blue-600" />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View senior candidates →</button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Expected Salary Range (10+ Yrs)</h3>
              <div className="space-y-3">
                {(cpi?.expectedSalaryRange || []).map((row, i) => (
                  <FunnelRow key={i} label={row.label} value={row.value} percentage={pctOf(row.value, sumOf(cpi?.expectedSalaryRange))} color="bg-blue-600" />
                ))}
              </div>
              <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View salary expectations →</button>
            </div>
          </div>

          {/* Top Skills in Demand / Recently Active */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm lg:col-span-1">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Top Skills in Demand</h3>
              <div className="grid grid-cols-2 gap-2">
                {(cpi?.topSkillsInDemand || []).map((skill, index) => (
                  <div key={index} className="flex justify-between items-center bg-slate-50 p-2 rounded-lg border border-slate-100 text-xs">
                    <span className="font-medium text-slate-700 truncate">{skill.label}</span>
                    <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold">{skill.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm lg:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-slate-900">Recently Active Candidates</h3>
                <button className="text-xs text-blue-600 font-semibold hover:underline">View all active candidates →</button>
              </div>
              <div className="space-y-3">
                {(cpi?.recentlyActiveCandidates || []).length > 0 ? (
                  (cpi?.recentlyActiveCandidates || []).map((candidate, idx) => (
                    <div key={candidate.candidateId ?? idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition">
                      <div className="flex items-center gap-3 min-w-0">
                        {candidate.photoUrl ? (
                          <img src={candidate.photoUrl} alt={candidate.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                        ) : (
                          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center text-xs font-black shrink-0">
                            {candidate.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                          </span>
                        )}
                        <div className="min-w-0">
                          <h4 className="text-sm font-bold text-slate-900 truncate">{candidate.name}</h4>
                          <p className="text-xs text-slate-500 truncate">{candidate.role} • {candidate.experience}</p>
                          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{candidate.location}</span>
                            <span>•</span>
                            <span>{candidate.lastActiveText}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        {candidate.matchScore ? (
                          <span className="inline-block text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-lg">
                            {candidate.matchScore} Match
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 font-semibold">—</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic py-2">No recently active candidates.</p>
                )}
              </div>
            </div>
          </div>

          {/* Pool Utilities */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Your Recent Activity</h3>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">{(cpi?.activitySummary.invitesSent ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Invites Sent</p></div>
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-emerald-600">{(cpi?.activitySummary.accepted ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Accepted</p></div>
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">{(cpi?.activitySummary.replied ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Replied</p></div>
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">{(cpi?.activitySummary.hired ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Hired</p></div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Company Activity</h3>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">{(cpi?.companyActivitySummary?.invitesSent ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Invites Sent</p></div>
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-emerald-600">{(cpi?.companyActivitySummary?.accepted ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Accepted</p></div>
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">{(cpi?.companyActivitySummary?.replied ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Replied</p></div>
                <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">{(cpi?.companyActivitySummary?.hired ?? 0).toLocaleString()}</p><p className="text-[10px] text-slate-500">Hired</p></div>
              </div>
            </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button onClick={() => navigateTo('/recruiter/candidates/listing')} className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 p-2 rounded-lg border border-slate-150 transition">
                  <Search className="w-3.5 h-3.5 text-blue-500" /> Search Candidates
                </button>
                <button onClick={() => navigateTo('/recruiter/invitations/listing')} className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 p-2 rounded-lg border border-slate-150 transition">
                  <Plus className="w-3.5 h-3.5 text-emerald-500" /> View Invitations
                </button>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Recent Searches</h3>
              <div className="space-y-1.5 text-xs text-slate-600">
                {(cpi?.recentSearches || []).length > 0 ? (
                  (cpi?.recentSearches || []).map((search, i) => (
                    <div key={i} className="flex justify-between items-center py-1 border-b border-dashed border-slate-100">
                      <span className="truncate">{search.label}</span><span className="text-slate-400 font-medium shrink-0 ml-2">{search.detail}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic py-1">No saved searches yet.</p>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Subscription Summary</h3>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="font-bold text-slate-800 truncate">{co.subscriptionPlanName || 'Professional Plan'}</span>
                  <span className="text-slate-400 shrink-0 ml-2">Expires {formatDate(co.subscriptionPlanExpireDate)}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(0, co.usagePercent))}%` }}></div>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2"><strong>{co.creditsRemaining.toLocaleString()}</strong> / {co.creditsPurchased.toLocaleString()} credits left</p>
            </div>
            </div>
          </div>
        </div>

        {/* ── Footer note ── */}
        <div className="mt-8 rounded-2xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white p-5 flex items-start gap-3">
          <Award className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm font-medium leading-relaxed">
            Pro tip: Invitations are deducted from your company's credit balance. Candidates who accept your
            invitation move to your hiring pipeline — keep the pending ones engaged to lift your acceptance rate.
          </p>
        </div>

      </div>
    </div>
  );
}

export default RecruiterDashboardComponent;